import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { Button, Spin, Tabs } from "antd";
import PurchaseOrderService from "../../services/purchaseOrder.service";
import { BlobToDownloadFile } from "../../helper/commanFunction";

// Task 2195 (F7, plan §17.2). No prototype markup exists for this screen (confirmed -- the
// prototype's only "report" references are a stone's certificate/lab report number, a different
// concept entirely) -- built as a simple generic table + CSV download per report, matching
// jewelry's own OrderReport.js CSV-export convention (BlobToDownloadFile, arraybuffer response).

const fmtCell = (columnName, v) => {
  if (v === null || v === undefined) return "-";
  if (typeof v === "number") {
    if (/Value|Cost/i.test(columnName)) return `$${v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    if (/Pct/i.test(columnName)) return `${v}%`;
    return v.toLocaleString();
  }
  return String(v);
};

const GenericTable = ({ rows }) => {
  const { t } = useTranslation();
  if (!rows || rows.length === 0) {
    return (
      <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, padding: 40, textAlign: "center", color: "#888" }}>
        {t("No data for this report yet.")}
      </div>
    );
  }
  const columns = Object.keys(rows[0]);
  return (
    <div style={{ background: "#fff", border: "1px solid #eee", borderRadius: 8, overflow: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c} style={{ textAlign: "left", padding: "10px 14px", borderBottom: "1px solid #eee", fontSize: 12, color: "#888" }}>
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i}>
              {columns.map((c) => (
                <td key={c} style={{ padding: "10px 14px", borderBottom: "1px solid #f3f3f3" }}>
                  {fmtCell(c, row[c])}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const ReportPanel = ({ dealerId, title, description, fetchFn, exportFn, exportName, extractRows }) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    if (!dealerId) return;
    setLoading(true);
    fetchFn({ DealerID: String(dealerId) })
      .then((r) => {
        setLoading(false);
        setRows(r.data.status ? extractRows(r.data.responseData) : []);
      })
      .catch(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealerId]);

  const handleExport = () => {
    setExporting(true);
    exportFn({ DealerID: String(dealerId) })
      .then((r) => {
        setExporting(false);
        BlobToDownloadFile(r.data, `${exportName}-${Date.now()}.csv`);
      })
      .catch(() => setExporting(false));
  };

  return (
    <Spin spinning={loading}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 10 }}>
        <div>
          <h3 style={{ margin: 0 }}>{title}</h3>
          <span style={{ color: "#888", fontSize: 12 }}>{description}</span>
        </div>
        <Button size="small" onClick={handleExport} loading={exporting} disabled={!rows || rows.length === 0}>
          {t("Export CSV")}
        </Button>
      </div>
      <GenericTable rows={rows} />
    </Spin>
  );
};

export const DiamondReports = () => {
  const { t } = useTranslation();
  const loginDetials = useSelector((state) => state.loginReducer);
  const dealerId = loginDetials?.loginDetials?.responseData?.dealerId;
  const memberType = loginDetials?.loginDetials?.responseData?.dealerMemberTypeName;
  const isRetailer = memberType === "Retailer" || memberType === "Combined";

  const supplierTabs = [
    {
      key: "ageing",
      label: t("Memo ageing"),
      children: (
        <ReportPanel
          dealerId={dealerId}
          title={t("Memo ageing")}
          description={t("Stones out on memo, bucketed by days since shipped, by retailer")}
          fetchFn={PurchaseOrderService.GetDiamondMemoAgeing}
          exportFn={PurchaseOrderService.ExportDiamondMemoAgeingCsv}
          exportName="MemoAgeing"
          extractRows={(d) => d}
        />
      ),
    },
    {
      key: "conversion",
      label: t("Memo conversion"),
      children: (
        <ReportPanel
          dealerId={dealerId}
          title={t("Memo conversion")}
          description={t("Lines settled sold vs. returned, by retailer — who gets memo next time")}
          fetchFn={PurchaseOrderService.GetDiamondMemoConversion}
          exportFn={PurchaseOrderService.ExportDiamondMemoConversionCsv}
          exportName="MemoConversion"
          extractRows={(d) => d}
        />
      ),
    },
    {
      key: "shipping",
      label: t("Shipping cost"),
      children: (
        <ReportPanel
          dealerId={dealerId}
          title={t("Shipping cost")}
          description={t("Billed vs. absorbed, outbound vs. return")}
          fetchFn={PurchaseOrderService.GetDiamondShippingCost}
          exportFn={PurchaseOrderService.ExportDiamondShippingCostCsv}
          exportName="ShippingCost"
          extractRows={(d) => d}
        />
      ),
    },
    {
      key: "discrepancies",
      label: t("Discrepancies"),
      children: (
        <ReportPanel
          dealerId={dealerId}
          title={t("Discrepancies")}
          description={t("By reason (still open or taken back), and time to resolve")}
          fetchFn={PurchaseOrderService.GetDiamondDiscrepancyReport}
          exportFn={PurchaseOrderService.ExportDiamondDiscrepancyReportCsv}
          exportName="Discrepancies"
          extractRows={(d) => d?.ByReason || []}
        />
      ),
    },
  ];

  const retailerTabs = [
    {
      key: "purchases",
      label: t("My purchases"),
      children: (
        <ReportPanel
          dealerId={dealerId}
          title={t("My purchases")}
          description={t("Stones bought, by supplier")}
          fetchFn={PurchaseOrderService.GetDiamondRetailerPurchases}
          exportFn={PurchaseOrderService.ExportDiamondRetailerPurchasesCsv}
          exportName="RetailerPurchases"
          extractRows={(d) => d}
        />
      ),
    },
  ];

  return (
    <div style={{ padding: 20 }}>
      <h2 style={{ margin: 0 }}>{t("Reports")}</h2>
      <p style={{ color: "#888", margin: 0, marginBottom: 16 }}>{t("Diamond trade reporting")}</p>
      <Tabs>
        {(isRetailer ? retailerTabs : supplierTabs).map((tab) => (
          <Tabs.TabPane tab={tab.label} key={tab.key}>
            {tab.children}
          </Tabs.TabPane>
        ))}
      </Tabs>
    </div>
  );
};

export default DiamondReports;
